import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import SearchableFilter from 'components/SearchableFilter';
import PaginationFooter from 'components/PaginationFooter';
import * as ManagementLayout from 'components/ManagementLayout';

import useSort from 'utils/useSort';

import useDebouncedValue from 'hooks/useDebouncedValue';
import { SearchProvider } from 'contexts/SearchContext';

import EmptyResults from 'components/EmptyResults';
import Search from '@decisiv/iconix/lib/components/Search';
import useToggle from 'hooks/useToggle';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedPlatformAccountsQuery } from './hooks/useManagedPlatformAccountsQuery';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from '../Teams/hooks/useTeamsFilters';

import useAccountsFilters from './hooks/useAccountsFilters';
import { generateColumns } from './columns';
import { ClearAllButton } from '../styles';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from '../constants';
import CreateAccountModal from './CreateAccountModal';

export type ManagedFleet = ExtractFromArray<
  ManagedPlatformAccountsQuery['managedPlatformAccounts']['collection']
>;

const Fleets = () => {
  const [createAccountModalOpen, toggleCreateAccountModal] = useToggle();

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleChangeAccountsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const [textValue, setTextValue, debouncedSearchValue] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const handleOnTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setTextValue(e.target.value);
      setPage(1);
    },
    [setTextValue, setPage],
  );

  const { data, loading } = useManagedPlatformAccountsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.accountIds,
      sentinelTeamsIds: pageParams.teamIds,
      oemSegmentsIds: pageParams.oemSegmentIds,
      text: debouncedSearchValue.length > 2 ? debouncedSearchValue : undefined,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const accountsFilters = useAccountsFilters({
    value: pageParams.accountIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    onSubmit: handleChangeAccountsFilter,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedPlatformAccounts.collection ?? [];
  const pagination = data?.managedPlatformAccounts.pagination;

  return (
    <>
      <ManagementLayout.TabBody.Container>
        <ManagementLayout.TabBody.Header.Container>
          <ManagementLayout.TabBody.Header.Left>
            <Button
              text={t`Create Account`}
              icon={Plus}
              size="small"
              onClick={toggleCreateAccountModal}
              disabled
            />
          </ManagementLayout.TabBody.Header.Left>
          <ManagementLayout.TabBody.Header.Right>
            <P size="small" marginRight={1}>
              <Trans>Search by Account Name, Account ID, and City:</Trans>
            </P>
            <SearchInput
              value={textValue}
              onChange={handleOnTextChange}
              hideLabel
              aria-label={t`Search by Account Name, Account ID, and City`}
              name="searchByFleet"
              size="small"
            />
          </ManagementLayout.TabBody.Header.Right>
        </ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Content.Container>
          <ManagementLayout.TabBody.Content.Filters>
            <SearchableFilter
              popoverLabel={t`OEM Segments`}
              placement="bottom-start"
              {...oemSegmentsFilterProps}
            />
            <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
            <SearchableFilter popoverLabel={t`Accounts`} {...accountsFilters} />
            {isFiltersDirty && (
              <ClearAllButton
                variant="ghost"
                onClick={handleClearFilters}
                text={t`Clear All`}
                size="small"
              />
            )}
          </ManagementLayout.TabBody.Content.Filters>
          {loading && (
            <Loading
              flex={1}
              justifyContent="center"
              size="large"
              title={t`Loading...`}
            />
          )}
          {!loading && tableData.length === 0 && (
            <EmptyResults
              title={t`No Accounts Found`}
              subtitle={t`No Managed Accounts Found`}
              icon={Search}
            />
          )}
          {!loading && tableData.length > 0 && (
            <SearchProvider searchString={debouncedSearchValue}>
              <NoWrapHeaderTable
                height="100%"
                scroll={['x', 'y']}
                data={tableData}
                columns={columns}
                onSort={onSort}
                defaultSort={defaultSort}
                footer={() => (
                  <PaginationFooter
                    // @ts-expect-error - JS file
                    title={t`Accounts`}
                    setCurrentPage={setPage}
                    totalOnPage={tableData.length}
                    page={pagination?.page}
                    perPage={pagination?.perPage}
                    totalEntries={pagination?.totalEntries}
                    totalPages={pagination?.totalPages}
                  />
                )}
              />
            </SearchProvider>
          )}
        </ManagementLayout.TabBody.Content.Container>
      </ManagementLayout.TabBody.Container>
      {
        /* istanbul ignore next -- @preserve */
        createAccountModalOpen && (
          <CreateAccountModal onClose={toggleCreateAccountModal} />
        )
      }
    </>
  );
};

export default Fleets;
